"use client";

import { useEffect, useState } from "react";
import { Moon, Sun } from "lucide-react";
import { m, AnimatePresence } from "framer-motion";

import { useSettingsStore, useIsHydrated } from "@/src/store/settings/store";

function resolveThemeMode(theme: string): "dark" | "light" {
  if (theme === "dark" || theme === "light") return theme;
  if (typeof window === "undefined") return "dark";
  try {
    if (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) {
      return "dark";
    }
  } catch {}
  const attr = document.documentElement.getAttribute("data-theme");
  if (attr === "dark" || attr === "light") return attr;
  return "light";
}

export function ThemeToggle() {
  const isHydrated = useIsHydrated();
  const theme = useSettingsStore(state => state.appearance.theme);
  const updateAppearance = useSettingsStore(state => state.updateAppearance);

  useEffect(() => {
    if (!isHydrated) return;
    const resolved = resolveThemeMode(theme);
    document.documentElement.setAttribute("data-theme", resolved);
    document.documentElement.classList.toggle("dark", resolved === "dark");
  }, [theme, isHydrated]);

  const toggleTheme = () => {
    const currentResolved = resolveThemeMode(theme);
    const nextTheme = currentResolved === "light" ? "dark" : "light";
    updateAppearance({ theme: nextTheme });
    document.documentElement.setAttribute("data-theme", nextTheme);
    document.documentElement.classList.toggle("dark", nextTheme === "dark");
    try {
      document.cookie = `kv-theme=${encodeURIComponent(nextTheme)}; path=/; max-age=31536000; SameSite=Lax`;
    } catch {}
  };

  if (!isHydrated) {
    return <div className="w-11 h-11 md:w-10 md:h-10 rounded-xl bg-surface border border-border shimmer-wrapper opacity-50" />;
  }

  const resolvedTheme = resolveThemeMode(theme);

  return (
    <m.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.9, rotate: 15 }}
      onClick={toggleTheme}
      className="w-11 h-11 md:w-10 md:h-10 flex items-center justify-center rounded-xl bg-surface border border-border hover:border-blue/30 hover:text-blue transition-colors group relative overflow-hidden flex-shrink-0"
      aria-label={`Switch to ${resolvedTheme === "light" ? "dark" : "light"} theme`}
    >
      <AnimatePresence mode="wait" initial={false}>
        <m.div
          key={resolvedTheme}
          initial={{ y: 20, opacity: 0, rotate: -90 }}
          animate={{ y: 0, opacity: 1, rotate: 0 }}
          exit={{ y: -20, opacity: 0, rotate: 90 }}
          transition={{ duration: 0.2, ease: "easeInOut" }}
        >
          {resolvedTheme === "light" ? (
            <Moon className="w-4 h-4 md:w-5 md:h-5" aria-hidden="true" />
          ) : (
            <Sun className="w-4 h-4 md:w-5 md:h-5" aria-hidden="true" />
          )}
        </m.div>
      </AnimatePresence>
    </m.button>
  );
}
